import { PublicAxiosInstance } from '@/api/axios'
import { logoImages, utilsImages } from '@/assets/images'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { endPoint } from '@/constant/end-point'
import { useTheme } from '@/hooks/use-theme'
import type { IServiceProviderAssessmentMigrateResponse } from '@/interface/service-provider-assessment.interface'
import { cn } from '@/lib/utils'
import axios from 'axios'
import { LogIn, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'

const VENDOR_INVITATION_TOKEN_KEY = 'vendorInvitationToken'

/**
 * Legacy service-provider assessment link - migration landing page.
 *
 * The old system emailed a long-lived, shareable link that let anyone submit an
 * assessment anonymously. We've since moved to an authenticated vendor portal.
 * Rather than accept anonymous submissions, this page validates the legacy token
 * on the backend (which mints a fresh single-use invitation), then routes the
 * vendor to registration or sign-in so their account is linked to the existing
 * assessment. No existing assessment data is lost.
 */
const ServiceProviderAssessmentPublicPage = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { theme } = useTheme()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<IServiceProviderAssessmentMigrateResponse | null>(null)

  const migrate = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await PublicAxiosInstance.get(endPoint.serviceProviderAssessmentPublic.migrate(token as string))
      setData(response.data?.data as IServiceProviderAssessmentMigrateResponse)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 410) {
        setError('This assessment link has expired. Please contact the organisation that sent it.')
      } else if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('This assessment link is no longer valid. Please contact the organisation that sent it.')
      } else {
        setError(
          axios.isAxiosError(err)
            ? (err.response?.data?.message ?? 'Failed to load this assessment link')
            : 'Failed to load this assessment link'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (token) migrate()
    else setLoading(false)
  }, [token])

  const handleContinue = () => {
    if (!data) return
    if (data.vendorExists) {
      // Existing account: persist the invitation token and go through sign-in;
      // the post-login flow claims it and links this assessment to the account.
      localStorage.setItem(VENDOR_INVITATION_TOKEN_KEY, data.invitationToken)
      navigate(`/sign-in?invitationToken=${encodeURIComponent(data.invitationToken)}`)
      return
    }
    // No account yet: send them to vendor registration with the invitation token.
    navigate(`/portal/register?token=${encodeURIComponent(data.invitationToken)}`)
  }

  if (!token) return <Navigate to={'/'} />

  if (loading) return <FullPageSkeleton />

  if (error)
    return (
      <div className='flex min-h-screen items-center justify-center p-4'>
        <div className='text-center'>
          <div className='mb-4 text-6xl text-red-600'>⚠️</div>
          <h2 className='mb-2 text-2xl font-bold'>Link unavailable</h2>
          <p className='mb-4 text-gray-600'>{error}</p>
          <Button onClick={() => navigate('/sign-in')}>
            <LogIn className='h-4 w-4' />
            Go to sign in
          </Button>
        </div>
      </div>
    )

  const isRegister = !data?.vendorExists

  return (
    <div className='flex min-h-screen w-full items-center justify-center bg-background p-4'>
      <div className={cn('flex w-full flex-col gap-6 xl:w-[60%] lg:w-[80%] md:w-full')}>
        <Card className='overflow-hidden p-0 shadow-md'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col items-center text-center'>
                  <img
                    loading='lazy'
                    decoding='async'
                    src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                    alt='Logo'
                    className='h-12 w-auto'
                  />
                  <h1 className='pt-3 text-2xl font-medium'>We&apos;ve moved to a new vendor portal</h1>
                  <p className='text-sm text-muted-foreground'>
                    This assessment is now completed from your own vendor account, where you can save progress, upload
                    evidence, and track review feedback in one place.
                  </p>
                </div>

                <div className='rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground'>
                  {isRegister ? (
                    <p>
                      We couldn&apos;t find an account for{' '}
                      <span className='font-medium text-foreground'>{data?.email}</span>. Register to access this
                      assessment, everything you&apos;ve already submitted is preserved.
                    </p>
                  ) : (
                    <p>
                      An account already exists for <span className='font-medium text-foreground'>{data?.email}</span>.
                      Sign in to continue this assessment from your dashboard.
                    </p>
                  )}
                </div>

                <Button className='w-full' onClick={handleContinue}>
                  {isRegister ? (
                    <>
                      <UserPlus className='h-4 w-4' />
                      Register
                    </>
                  ) : (
                    <>
                      <LogIn className='h-4 w-4' />
                      Sign in
                    </>
                  )}
                </Button>

                <p className='text-center text-xs text-muted-foreground'>
                  The previous direct link is no longer used for submissions.
                </p>
              </div>
            </div>
            <div className='relative hidden md:block'>
              <img
                loading='lazy'
                decoding='async'
                src={utilsImages.loginPageSide}
                alt='Vendor portal'
                className='absolute inset-0 h-full w-full object-cover dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ServiceProviderAssessmentPublicPage
